/**
 * ScanIdChip — quiet, support-metadata style display of a Mission Watch
 * `scan_id`. Never a primary customer-facing element.
 *
 * - Customer surface: renders a short 8-char prefix in muted monospace
 *   (`showCopy=false`). No copy affordance — this is signal-of-trust,
 *   not something we're inviting customers to click.
 * - Admin surface: same short display + a small copy button that puts
 *   the full UUID onto the clipboard (`showCopy=true`).
 *
 * The chip guarantees it always renders the `scan_id` of the CURRENT
 * authoritative report — never the id of an internal-failure diagnostic
 * attempt (those don't get persisted into `health_reports` at all, per
 * Iter-71i).
 */
import { useState } from "react";
import { Copy, Check } from "lucide-react";

export function ScanIdChip({ scanId, showCopy = false, testid = "scan-id-chip" }) {
  const [copied, setCopied] = useState(false);
  if (!scanId || typeof scanId !== "string") return null;
  const short = scanId.slice(0, 8);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(scanId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — quiet no-op */
    }
  };

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono text-charcoal-400"
      data-testid={testid}
      title={showCopy ? scanId : `Scan ${scanId}`}
    >
      <span className="uppercase tracking-widest">Scan</span>
      <span className="text-charcoal-500" data-testid={`${testid}-short`}>{short}</span>
      {showCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="ml-0.5 p-0.5 rounded hover:bg-charcoal-100 text-charcoal-400 hover:text-charcoal-700 transition-colors"
          aria-label="Copy full scan ID"
          data-testid={`${testid}-copy`}
        >
          {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
        </button>
      )}
    </span>
  );
}

export default ScanIdChip;
